import React, { useContext, useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import MapContext from '../../context/MapContext';
import WeatherContext from '../../context/WeatherContext';
import { TileLayer, LayersControl } from 'react-leaflet';
import { disableLayers } from '../../manager/WeatherManager';
import { ensureLeafletPane } from './MvtHybridDemo';
import { WEATHER_PANE_Z_INDEX, WEATHER_TILE_Z_INDEX } from '../util/ZIndexes';

const WEATHER_PANE = 'weatherPane';

function getWeatherTime(weatherDateObj) {
    let h = weatherDateObj.getUTCHours();
    if (h < 10) {
        h = '0' + h;
    }
    let m = weatherDateObj.getUTCMonth() + 1;
    if (m < 10) {
        m = '0' + m;
    }
    let d = weatherDateObj.getUTCDate();
    if (d < 10) {
        d = '0' + d;
    }
    return weatherDateObj.getUTCFullYear() + '' + m + '' + d + '_' + h + '00';
}

const WeatherLayer = () => {
    const map = useMap();
    const wtx = useContext(WeatherContext);
    const mtx = useContext(MapContext);

    const [time, setTime] = useState(null);
    const [paneReady, setPaneReady] = useState(false);

    // pane must exist before react-leaflet creates TileLayer instances
    useEffect(() => {
        if (map) {
            ensureLeafletPane(map, WEATHER_PANE, WEATHER_PANE_Z_INDEX);
            setPaneReady(true);
        }
    }, [map]);

    useEffect(() => {
        wtx.setWeatherLayers((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((type) => {
                if (type !== wtx.weatherType) {
                    next[type] = next[type].map((l, index) => {
                        if (!l.checked) return l;
                        const canEnable = !disableLayers(next[wtx.weatherType][index], wtx);
                        const cleared = { ...l, checked: false };
                        if (canEnable) {
                            const tgt = next[wtx.weatherType][index];
                            next[wtx.weatherType][index] = { ...tgt, checked: true };
                        }
                        return cleared;
                    });
                }
            });
            return next;
        });
    }, [wtx.weatherType]);

    useEffect(() => {
        if (map) {
            map.on('zoomend', () => mtx.setMapBbox(map.getBounds()));
            map.on('dragend', () => mtx.setMapBbox(map.getBounds()));
        }
    }, [map]);

    useEffect(() => {
        if (map) {
            map.eachLayer((layer) => {
                if (layer.options?.name?.match(/^weather-/) && layer.options.time) {
                    const newTime = getWeatherTime(wtx.weatherDate);
                    const key = layer.options.name.split('-')[1];
                    map.eachLayer((fade) => {
                        if (fade.options?.name === 'fade-' + key) {
                            const FADE_INTERVAL_MS = 100;
                            let tries = 10; // maximum 1 second

                            const tryFade = () => {
                                if (layer.isLoading() === false || tries === 0) {
                                    if (fade.options.time !== newTime) {
                                        fade.options.time = newTime;
                                        fade.redraw();
                                    }
                                } else {
                                    tries--;
                                    setTimeout(tryFade, FADE_INTERVAL_MS);
                                }
                            };

                            setTimeout(tryFade, FADE_INTERVAL_MS);
                        }
                    });
                    if (layer.options.time !== newTime) {
                        layer.options.time = newTime;
                        layer.redraw();
                    }
                }
            });
        }
        setTime(getWeatherTime(wtx.weatherDate));
    }, [wtx.weatherDate]);

    const opacityDivider = 0.6; // main+fade layers (*0.5 + *0.5) result less opacity than 1 layer *1.0

    if (!paneReady) {
        return null;
    }

    return (
        <>
            <LayersControl>
                {Object.keys(wtx.weatherLayers).map((k) => {
                    return wtx.weatherLayers[k].map((item) => (
                        <React.Fragment key={'weather_fader_' + item.key}>
                            <LayersControl.Overlay
                                key={'weather_main_' + item.key + item.checked}
                                checked={item.checked}
                            >
                                <TileLayer
                                    id={'se-weather-l-' + item.key}
                                    name={'weather-' + item.key}
                                    url={item.url}
                                    time={time}
                                    tms={true}
                                    pane={WEATHER_PANE}
                                    zIndex={WEATHER_TILE_Z_INDEX}
                                    minZoom={2}
                                    opacity={item.opacity * opacityDivider}
                                    maxNativeZoom={item.maxNativeZoom}
                                    maxZoom={item.maxZoom}
                                />
                            </LayersControl.Overlay>
                            <LayersControl.Overlay
                                key={'weather_fade_' + item.key + item.checked}
                                checked={item.checked}
                            >
                                <TileLayer
                                    name={'fade-' + item.key}
                                    url={item.url}
                                    time={time}
                                    tms={true}
                                    pane={WEATHER_PANE}
                                    zIndex={WEATHER_TILE_Z_INDEX}
                                    minZoom={2}
                                    opacity={item.opacity * opacityDivider}
                                    maxNativeZoom={item.maxNativeZoom}
                                    maxZoom={item.maxZoom}
                                />
                            </LayersControl.Overlay>
                        </React.Fragment>
                    ));
                })}
            </LayersControl>
        </>
    );
};

export default WeatherLayer;
